import type { AuthEnv } from './types';

/**
 * SSO login marker cookie, set on `.onepeloton.com` by auth-orca (`/sso/login_site`) at login.
 * The name is the same in every environment — only auth-orca's HttpOnly session/user-hash
 * cookies are env-prefixed. Source of truth: `web-sso.visited-sites-cookie-name` in
 * https://github.com/pelotoncycle/auth-orchestration/blob/main/src/main/resources/application-prod.yml
 * and .../application-stage.yml (stage also serves UAT / deploy previews / local dev).
 */
export const LOGIN_MARKER_COOKIE_NAME = 'peloton_visited_sites';

/**
 * @param _authEnv Unused: the marker cookie name is not env-specific (see above). Kept so
 * existing callers keep compiling.
 * @deprecated Use `LOGIN_MARKER_COOKIE_NAME`.
 */
export const getLoggedInCookieName = (_authEnv?: AuthEnv): string =>
  LOGIN_MARKER_COOKIE_NAME;

/**
 * Returns true if the SSO marker cookie is present on the current document.
 *
 * Mirrors the legacy `@core/auth/helpers.ts#getContainsLoginCookie`.
 *
 * Wrapped in try/catch because `document.cookie` access can throw in
 * sandboxed iframes, blocked-cookie storage modes, and certain corporate
 * security extensions. A read failure here must never block AuthProvider
 * init — if we can't tell whether the marker exists, fall back to "no
 * cookie" (skip silent auth, same as a guest cold load).
 */
export const getContainsLoginCookie = (_authEnv?: AuthEnv): boolean => {
  try {
    if (typeof document === 'undefined' || !document.cookie) {
      return false;
    }
    return !!document.cookie
      .split(' ')
      .find(c => c.split('=')[0] === LOGIN_MARKER_COOKIE_NAME);
  } catch {
    return false;
  }
};
